/**
 * 유저 앱 하단탭 중앙 ＋ 맥락 메뉴 SSOT (v5.1 §1-4)
 * @see docs/USER_FAB_CONTEXT_MENU_SSOT_KO.md
 * @typedef {{ id: string, labelKey: string, to?: string, icon?: string, feature?: string, requireAuth?: boolean }} UserFabAction
 */

import { buildHubSearchPath, normalizeHubCategoryId } from '../lib/hubCategoryRoute';

const WRITE_PATH_RE = /\/(write|create|edit)(\/|$)/;

/** @param {string} pathname */
export function shouldHideUserFab(pathname) {
  const p = String(pathname || '');
  if (!p || p === '/shorts/create') return true;
  if (WRITE_PATH_RE.test(p)) return true;
  if (p.startsWith('/admin')) return true;
  if (p.startsWith('/business/dashboard')) return true;
  return false;
}

/**
 * @param {string} pathname
 * @returns {string}
 */
function hubCategoryFromPath(pathname) {
  const m = String(pathname || '').match(/\/hub\/([^/]+)/);
  return m ? normalizeHubCategoryId(m[1]) : '';
}

/**
 * @param {UserFabAction[]} actions
 * @param {(f: string) => boolean} enabled
 */
function pushTradeActions(actions, enabled) {
  if (!enabled('trade')) return;
  actions.push(
    { id: 'product_write', labelKey: 'userFab.productWrite', to: '/write', requireAuth: true },
    { id: 'my_trades', labelKey: 'userFab.myTrades', to: '/my-trades', requireAuth: true },
  );
}

/**
 * @param {UserFabAction[]} actions
 * @param {(f: string) => boolean} enabled
 */
function pushRealEstateActions(actions, enabled) {
  if (!enabled('real_estate')) return;
  actions.push(
    { id: 'property_write', labelKey: 'userFab.propertyWrite', to: '/real-estate/write', requireAuth: true },
    { id: 'my_properties', labelKey: 'userFab.myProperties', to: '/my-properties', requireAuth: true },
  );
}

/**
 * @param {UserFabAction[]} actions
 * @param {(f: string) => boolean} enabled
 * @param {{ includeGroupCreate?: boolean, includeMyPosts?: boolean }} [opts]
 */
function pushCommunityFeedActions(actions, enabled, opts = {}) {
  const { includeGroupCreate = true, includeMyPosts = true } = opts;
  if (!enabled('community')) return;
  actions.push(
    { id: 'community_write', labelKey: 'userFab.communityWrite', to: '/community/write', requireAuth: true },
  );
  if (includeMyPosts) {
    actions.push(
      { id: 'my_community_posts', labelKey: 'userFab.myCommunityPosts', to: '/my/community-posts', requireAuth: true },
    );
  }
  if (includeGroupCreate) {
    actions.push(
      { id: 'group_create', labelKey: 'userFab.groupCreate', to: '/groups/create', requireAuth: true },
    );
  }
}

/**
 * @param {UserFabAction[]} actions
 * @param {{ category?: string }} [opts]
 */
function pushSearchAction(actions, opts = {}) {
  const category = String(opts.category || '').trim();
  actions.push({
    id: 'search',
    labelKey: 'userFab.search',
    to: buildHubSearchPath({ category }),
    requireAuth: false,
  });
}

/**
 * @param {UserFabAction[]} actions
 * @param {string} [registerTo]
 * @param {{ register?: string, my?: string }} [labelKeys]
 */
function pushBusinessActions(actions, registerTo = '/business/register', labelKeys = {}) {
  actions.push(
    {
      id: 'business_register',
      labelKey: labelKeys.register || 'userFab.businessRegister',
      to: registerTo,
      requireAuth: true,
    },
    {
      id: 'my_businesses',
      labelKey: labelKeys.my || 'userFab.myBusinesses',
      to: '/my-businesses',
      requireAuth: true,
    },
  );
}

/** @param {string} pathname */
function isGroupDetailPath(pathname) {
  const m = String(pathname || '').match(/^\/groups\/([^/]+)$/);
  if (!m) return false;
  return m[1] !== 'create';
}

/**
 * @param {{ pathname: string, search?: string, isFeatureEnabled: (f: string) => boolean }} ctx
 * @returns {UserFabAction[]}
 */
export function resolveUserFabActions(ctx) {
  const pathname = String(ctx.pathname || '');
  const search = String(ctx.search || '');
  const enabled = ctx.isFeatureEnabled || (() => true);
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);

  /** @type {UserFabAction[]} */
  const actions = [];

  if (pathname === '/real-estate' || pathname.startsWith('/my-properties')) {
    pushRealEstateActions(actions, enabled);
    return actions;
  }

  if (pathname === '/community' || pathname.startsWith('/my/community-posts')) {
    if (!enabled('community')) return [];
    if (params.get('tab') === 'group') {
      actions.push(
        { id: 'group_create', labelKey: 'userFab.groupCreate', to: '/groups/create', requireAuth: true },
        { id: 'my_groups', labelKey: 'userFab.myGroups', to: '/my/groups', requireAuth: true },
        { id: 'search_groups', labelKey: 'userFab.searchGroups', to: '/community?tab=group&focus=search', requireAuth: false },
      );
      return actions;
    }
    pushCommunityFeedActions(actions, enabled);
    return actions;
  }

  if (pathname === '/shorts' || pathname.startsWith('/mingo-story')) {
    if (enabled('story') || enabled('trade')) {
      actions.push({ id: 'story_write', labelKey: 'userFab.storyWrite', to: '/shorts/create', requireAuth: true });
    }
    return actions;
  }

  const hubCat = hubCategoryFromPath(pathname);
  if (hubCat) {
    const registerTo = `/business/register?category=${encodeURIComponent(hubCat)}`;
    const hubLabels = { register: 'userFab.hubRegister', my: 'userFab.hubMyRegister' };
    if (hubCat.includes('realestate') || hubCat === 'living_realestate_rental') {
      pushRealEstateActions(actions, enabled);
    }
    pushBusinessActions(actions, registerTo, hubLabels);
    pushSearchAction(actions, { category: hubCat });
    return actions;
  }

  if (pathname === '/home') {
    if (enabled('story') || enabled('trade')) {
      actions.push({ id: 'story_write', labelKey: 'userFab.storyWrite', to: '/shorts/create', requireAuth: true });
    }
    pushTradeActions(actions, enabled);
    pushBusinessActions(actions);
    pushSearchAction(actions);
    return actions;
  }

  if (
    pathname === '/life'
    || pathname === '/local-info'
    || pathname === '/all-services'
    || pathname.startsWith('/expat-info')
    || pathname.startsWith('/safepath-life')
  ) {
    pushBusinessActions(actions);
    pushSearchAction(actions);
    return actions;
  }

  if (pathname.startsWith('/product/') || pathname.startsWith('/property/')) {
    return [];
  }

  if (pathname === '/my/groups' || isGroupDetailPath(pathname)) {
    return [];
  }

  pushTradeActions(actions, enabled);
  pushSearchAction(actions);
  return actions;
}
